import { BarChart3 } from 'lucide-react';

import type {
  Persona,
  ResearchPoll,
  ResearchPollVote,
  ResearchRoomMessage,
} from '@repo/contracts';
import { Tooltip } from '@repo/ui/tooltip';

import { PersonaAvatar } from '../persona/persona-avatar';
import {
  averageRating,
  counts,
  isTorn,
  overallOrder,
  summarisePoll,
} from './poll-summary';
import { FocusChip } from './room-messages';

const FORMAT_LABEL = {
  choice: 'Pick one',
  ranking: 'Ranking',
  rating: 'Rating',
  'yes-no': 'Yes / No',
} as const;

function percent(n: number): string {
  return `${String(Math.round(n * 100))}%`;
}

/** A voter's face, fainter the less sure they were. */
function Voter({
  onOpen,
  persona,
  sureness,
  vote,
}: {
  onOpen?: (id: string) => void;
  persona?: Persona;
  sureness: number;
  vote: ResearchPollVote;
}) {
  if (!persona) return null;
  const torn = isTorn(vote);
  return (
    <Tooltip label={`${persona.name} · ${percent(sureness)} sure`}>
      <button
        aria-label={`${persona.name}, ${percent(sureness)} sure${torn ? ', torn' : ''}`}
        className="relative cursor-pointer rounded-md transition hover:scale-105"
        onClick={() => {
          onOpen?.(persona.id);
        }}
        type="button"
      >
        <PersonaAvatar
          className={[
            'ring-2 ring-surface',
            sureness >= 0.75
              ? ''
              : sureness >= 0.5
                ? 'opacity-75'
                : 'opacity-50',
          ].join(' ')}
          persona={persona}
          size="sm"
        />
        {torn ? (
          <span className="absolute -right-1 -bottom-1 rounded-pill bg-warning px-1 text-badge leading-tight font-semibold text-white ring-2 ring-surface">
            ?
          </span>
        ) : null}
      </button>
    </Tooltip>
  );
}

function ChoiceResults({
  onOpen,
  personas,
  poll,
}: {
  onOpen?: (id: string) => void;
  personas: Map<string, Persona>;
  poll: ResearchPoll;
}) {
  const tally = counts(poll);
  const most = Math.max(...tally);
  const total = Math.max(poll.votes.length, 1);
  return (
    <ul className="space-y-2">
      {poll.options.map((option, i) => {
        const voters = poll.votes.filter((vote) => vote.pick === i);
        const leading = tally[i] === most && most > 0;
        return (
          <li key={option}>
            <div className="flex items-baseline justify-between gap-2">
              <span
                className={`truncate text-ui ${leading ? 'font-semibold text-text-primary' : 'text-text-secondary'}`}
              >
                {option}
              </span>
              <span className="shrink-0 text-tag text-text-muted tabular-nums">
                {tally[i]}
              </span>
            </div>
            <div className="mt-1 flex items-center gap-2">
              <div className="h-2 flex-1 overflow-hidden rounded-pill bg-warm">
                <div
                  className={`h-full rounded-pill transition-all duration-500 ${leading ? 'bg-brand' : 'bg-text-muted/50'}`}
                  style={{ width: percent((tally[i] ?? 0) / total) }}
                />
              </div>
              <span className="flex -space-x-1.5">
                {voters.map((vote) => (
                  <Voter
                    key={vote.personaId}
                    onOpen={onOpen}
                    persona={personas.get(vote.personaId)}
                    sureness={vote.weights[i] ?? 0}
                    vote={vote}
                  />
                ))}
              </span>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

function RatingResults({
  onOpen,
  personas,
  poll,
}: {
  onOpen?: (id: string) => void;
  personas: Map<string, Persona>;
  poll: ResearchPoll;
}) {
  const average = averageRating(poll);
  const position = (value: number) => percent((value - 1) / 4);
  // People who land in the same place share one spot, side by side, rather
  // than covering each other. Half-steps are close enough to count as "the
  // same" to a reader.
  const clusters = [...poll.votes]
    .sort((a, b) => (a.value ?? 3) - (b.value ?? 3))
    .reduce<{ at: number; votes: ResearchPollVote[] }[]>((groups, vote) => {
      const at = Math.round((vote.value ?? 3) * 2) / 2;
      const group = groups.find((g) => g.at === at);
      if (group) group.votes.push(vote);
      else groups.push({ at, votes: [vote] });
      return groups;
    }, []);
  return (
    <div>
      <div className="relative mx-8 h-12">
        <div className="absolute top-5 right-0 left-0 h-1.5 rounded-pill bg-warm" />
        {[1, 2, 3, 4, 5].map((n) => (
          <span
            className="absolute top-8 -translate-x-1/2 text-tag text-text-muted tabular-nums"
            key={n}
            style={{ left: position(n) }}
          >
            {n}
          </span>
        ))}
        {average !== null ? (
          <Tooltip label={`Average ${average.toFixed(1)}`}>
            <span
              className="absolute top-3.5 h-4 w-0.5 -translate-x-1/2 rounded-pill bg-brand"
              style={{ left: position(average) }}
            />
          </Tooltip>
        ) : null}
        {clusters.map(({ at, votes }) => (
          <span
            className="absolute top-0 flex -translate-x-1/2 -space-x-2 transition-all duration-500"
            key={at}
            style={{ left: position(at) }}
          >
            {votes.map((vote) => (
              <Voter
                key={vote.personaId}
                onOpen={onOpen}
                persona={personas.get(vote.personaId)}
                sureness={Math.max(...vote.weights)}
                vote={vote}
              />
            ))}
          </span>
        ))}
      </div>
      <div className="mt-1 flex justify-between text-tag text-text-muted">
        <span>{poll.options[0]}</span>
        <span>{poll.options[4]}</span>
      </div>
    </div>
  );
}

function RankingResults({
  onOpen,
  personas,
  poll,
}: {
  onOpen?: (id: string) => void;
  personas: Map<string, Persona>;
  poll: ResearchPoll;
}) {
  const order = overallOrder(poll);
  return (
    <ol className="space-y-1.5">
      {order.map((item, place) => {
        const firsts = poll.votes.filter((v) => (v.order ?? [])[0] === item);
        return (
          <li className="flex items-center gap-2.5" key={item}>
            <span
              className={`flex size-6 shrink-0 items-center justify-center rounded-pill text-tag font-semibold tabular-nums ${place === 0 ? 'bg-brand text-white' : 'bg-warm text-text-secondary'}`}
            >
              {place + 1}
            </span>
            <span
              className={`min-w-0 flex-1 truncate text-ui ${place === 0 ? 'font-semibold text-text-primary' : 'text-text-secondary'}`}
            >
              {poll.options[item]}
            </span>
            <span className="flex -space-x-1.5">
              {firsts.map((vote) => (
                <Voter
                  key={vote.personaId}
                  onOpen={onOpen}
                  persona={personas.get(vote.personaId)}
                  sureness={vote.weights[item] ?? 0}
                  vote={vote}
                />
              ))}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

/**
 * A poll's results: who chose what and how sure they were, one line on what
 * it means, each voter's reason as it arrives, and the follow-ups the result
 * suggests — a torn voter, someone who went against the room.
 */
export function PollCard({
  message,
  onOpenProfile,
  onSpotlight,
  personas,
  reasonsPending,
}: {
  message: ResearchRoomMessage;
  /** Takes the canvas to the part of the screen the poll is about. */
  onSpotlight?: () => void;
  onOpenProfile?: (id: string) => void;
  personas: Map<string, Persona>;
  /** Whether reasons may still be arriving (the round is still running). */
  reasonsPending: boolean;
}) {
  const poll = message.poll;
  if (!poll) return null;

  const counted = message.status === 'done' && poll.votes.length > 0;
  const summary = counted ? summarisePoll(poll, personas) : null;

  return (
    <article className="rounded-card bg-surface px-4 py-3.5 ring-1 ring-hairline duration-200 animate-in fade-in slide-in-from-bottom-1 motion-reduce:animate-none">
      <header className="flex items-center gap-2">
        <BarChart3 aria-hidden="true" className="size-4 text-text-muted" />
        <p className="flex-1 text-ui">
          <span className="font-semibold text-text-primary">Poll results</span>
          <span className="text-text-muted">
            {' '}
            · {FORMAT_LABEL[poll.format]}
          </span>
        </p>
        {message.focus && onSpotlight ? (
          <FocusChip label={message.focus.label} onClick={onSpotlight} />
        ) : counted ? (
          <span className="text-tag text-text-muted">
            {poll.votes.length} voted
          </span>
        ) : null}
      </header>

      {message.status === 'failed' ? (
        <p className="mt-2 text-ui text-text-muted italic">
          The votes couldn&apos;t be counted this time.
        </p>
      ) : !counted ? (
        <p className="mt-2 animate-pulse text-ui text-text-muted motion-reduce:animate-none">
          Counting votes…
        </p>
      ) : (
        <>
          <p className="mt-2 text-ui-strong font-semibold text-text-primary">
            {summary?.headline}
          </p>
          <div className="mt-3">
            {poll.format === 'rating' ? (
              <RatingResults
                onOpen={onOpenProfile}
                personas={personas}
                poll={poll}
              />
            ) : poll.format === 'ranking' ? (
              <RankingResults
                onOpen={onOpenProfile}
                personas={personas}
                poll={poll}
              />
            ) : (
              <ChoiceResults
                onOpen={onOpenProfile}
                personas={personas}
                poll={poll}
              />
            )}
          </div>

          <ul className="mt-4 space-y-2 border-t border-hairline pt-3">
            {poll.votes.map((vote) => {
              const persona = personas.get(vote.personaId);
              if (!persona) return null;
              return (
                <li className="flex gap-2" key={vote.personaId}>
                  <PersonaAvatar persona={persona} size="sm" />
                  <p className="min-w-0 flex-1 text-caption leading-relaxed text-text-secondary">
                    <span className="font-semibold text-text-primary">
                      {persona.name.split(' ')[0]}
                    </span>{' '}
                    {vote.reason ? (
                      vote.reason
                    ) : reasonsPending ? (
                      <span className="animate-pulse text-text-muted motion-reduce:animate-none">
                        is explaining…
                      </span>
                    ) : null}
                  </p>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </article>
  );
}
